import React, { useCallback, useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import type { SectionStatus } from '@src/packages/dashboard/components/ReportSummary/SectionStatus';
import { getHistorySectionErrorMessageId } from '@src/packages/dashboard/components/ReportSummary/historySectionErrorMessageIds';
import type {
  ExecutionsHistoryRouteQuery,
  ExecutionsHistoryRouteSection,
  ExecutionsHistoryScheduleSection,
  RouteDrilldownSectionData,
  RouteDrilldownTarget,
  ScheduleDrilldownSectionData,
  ScheduleDrilldownTarget,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import ScheduleSummary from '../../SchedulesDashboard/components/ScheduleSummary';
import type { ScheduleSummaryData } from '../../SchedulesDashboard/components/ScheduleSummary/types';
import type { ChartView } from '@src/packages/dashboard/components/ReportSummary/types';
import type { CheckpointRoutesChartFilters } from '../checkpointRoutesFilterConfig';
import type { RouteSectionsState } from '../hooks/useRouteHistorySections';
import { useRouteHistoryDrilldown } from '../hooks/useRouteHistoryDrilldown';
import { buildRouteSummaryData, ROUTE_TO_SCHEDULE_SECTION } from './routeSummaryData';

interface RouteSummaryProps {
  filters: CheckpointRoutesChartFilters;
  divisionIds: string[];
  query?: ExecutionsHistoryRouteQuery;
  sections: RouteSectionsState;
  retrySection: (section: ExecutionsHistoryRouteSection) => void;
  data?: ScheduleSummaryData;
  complianceChartView?: ChartView;
  onComplianceChartViewChange?: (view: ChartView) => void;
}

interface DrilldownRequest {
  target: ScheduleDrilldownTarget | null;
  search: string;
  page: number;
}

const toRouteDrilldownTarget = (
  target: ScheduleDrilldownTarget | null,
): RouteDrilldownTarget | null => {
  if (!target) return null;
  return target.kind === 'schedules' ? { kind: 'routes', filter: target.filter } : target;
};

const toScheduleDrilldownData = (
  data?: RouteDrilldownSectionData,
): ScheduleDrilldownSectionData | undefined => {
  if (!data) return undefined;
  if (data.kind === 'routes') {
    return {
      kind: 'schedules',
      total: data.total,
      rows: data.rows.map(route => ({ ...route, scheduleId: route.routeId })),
    };
  }
  if (data.kind === 'users') {
    return {
      kind: 'users',
      total: data.total,
      rows: data.rows.map(user => ({ ...user, schedules: user.routes })),
    };
  }
  return data;
};

const RouteSummary: React.FC<RouteSummaryProps> = ({
  filters,
  divisionIds,
  query,
  sections,
  retrySection,
  data: dataOverride,
  complianceChartView,
  onComplianceChartViewChange,
}) => {
  const intl = useIntl();
  const data = useMemo<ScheduleSummaryData>(
    () => dataOverride ?? buildRouteSummaryData(sections),
    [dataOverride, sections],
  );
  const sectionStatuses = useMemo(() => {
    const statuses = {} as Partial<Record<ExecutionsHistoryScheduleSection, SectionStatus>>;
    (Object.keys(ROUTE_TO_SCHEDULE_SECTION) as ExecutionsHistoryRouteSection[]).forEach(
      routeSection => {
        const state = sections[routeSection];
        let status: SectionStatus;
        if (state.loading || !state.result) {
          status = { kind: 'loading' };
        } else if (state.result.status === 'error') {
          status = {
            kind: 'error',
            message: intl.formatMessage({ id: getHistorySectionErrorMessageId(state.result.errorCode) }),
            retryLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.section.retry' }),
            onRetry: () => retrySection(routeSection),
          };
        } else {
          status = { kind: 'ready' };
        }
        statuses[ROUTE_TO_SCHEDULE_SECTION[routeSection]] = status;
      },
    );
    return statuses;
  }, [retrySection, sections, intl]);
  const [drilldownRequest, setDrilldownRequest] = useState<DrilldownRequest>({
    target: null,
    search: '',
    page: 1,
  });
  const drilldownTarget = useMemo(
    () => toRouteDrilldownTarget(drilldownRequest.target),
    [drilldownRequest.target],
  );
  const routeDrilldown = useRouteHistoryDrilldown(
    query,
    drilldownTarget,
    drilldownRequest.search,
    drilldownRequest.page,
  );
  const handleDrilldownRequest = useCallback((request: DrilldownRequest) => {
    setDrilldownRequest(request);
  }, []);

  return (
    <ScheduleSummary
      filters={filters}
      divisionIds={divisionIds}
      variant="routes"
      dataOverride={data}
      complianceChartView={complianceChartView}
      onComplianceChartViewChange={onComplianceChartViewChange}
      sectionStatuses={sectionStatuses}
      onDrilldownRequest={handleDrilldownRequest}
      externalDrilldown={{
        data: toScheduleDrilldownData(routeDrilldown.data),
        error: routeDrilldown.result?.status === 'error',
        errorCode: routeDrilldown.result?.status === 'error' ? routeDrilldown.result.errorCode : undefined,
        loading: routeDrilldown.loading,
        pageSize: routeDrilldown.pageSize,
        retry: routeDrilldown.retry,
      }}
    />
  );
};

export default RouteSummary;
